import { useState, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageHeader } from "../components/shared/PageHeader";
import { AdminTable } from "../components/shared/AdminTable";
import { SlideOver } from "../components/shared/SlideOver";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { FormField } from "../components/shared/FormField";
import { MapPinPlus } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ImageUpload } from "../../../components/common/ImageUpload";
import { useAdminT } from "../../../hooks/useAdminT";
import { useFormValidation } from "../../../hooks/useFormValidation";
import { RemoteImage } from "../../../core/components/RemoteImage";
import { areaSchema } from "../../../lib/validationSchemas";
import toast from "react-hot-toast";

const getTrans = (translations: any[], locale: string, field: string) =>
    translations?.find((t: any) => t.locale === locale)?.[field] || "";

export default function AreasPage() {
    const queryClient = useQueryClient();
    const { t } = useAdminT();
    const { handleServerErrors } = useFormValidation();
    const [isDrawOpen, setIsDrawOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<any>(null);

    const { register, handleSubmit, reset, control, setError, formState: { errors } } = useForm({
        resolver: zodResolver(areaSchema),
    });

    const { data: areas, isLoading } = useQuery({
        queryKey: ["areas"],
        queryFn: () => api.get("/api/admin/areas").then(res => res.data.data)
    });

    const createMutation = useMutation({
        mutationFn: (data: any) => api.post("/api/admin/areas", data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["areas"] });
            toast.success(t("common.saveSuccess") || "Saved successfully");
            handleClose();
        },
        onError: (error: any) => handleServerErrors(error, setError, "Failed to save"),
    });

    const updateMutation = useMutation({
        mutationFn: (data: any) => api.put(`/api/admin/areas/${editingId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["areas"] });
            toast.success(t("common.saveSuccess") || "Saved successfully");
            handleClose();
        },
        onError: (error: any) => handleServerErrors(error, setError, "Failed to save"),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => api.delete(`/api/admin/areas/${id}`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["areas"] });
            toast.success("Deleted successfully");
            setDeleteTarget(null);
        },
        onError: () => {
            toast.error("Failed to delete area");
            setDeleteTarget(null);
        }
    });

    const handleClose = () => { setIsDrawOpen(false); setEditingId(null); reset(); };

    const handleEdit = useCallback((area: any) => {
        setEditingId(area.id);
        reset({
            slug: area.slug,
            image: area.image || "",
            order: area.order ?? 0,
            active: area.active ?? true,
            nameEn: getTrans(area.translations, "en", "name"),
            nameAr: getTrans(area.translations, "ar", "name"),
            descriptionEn: getTrans(area.translations, "en", "description"),
            descriptionAr: getTrans(area.translations, "ar", "description"),
        });
        setIsDrawOpen(true);
    }, [reset]);

    const onSubmit = (data: any) => {
        const payload: any = {
            slug: data.slug,
            image: data.image || null,
            order: data.order ? Number(data.order) : 0,
            active: data.active ?? true,
            translations: [
                { locale: "en", name: data.nameEn, description: data.descriptionEn || null },
                { locale: "ar", name: data.nameAr, description: data.descriptionAr || null },
            ],
        };
        if (editingId) { updateMutation.mutate(payload); } else { createMutation.mutate(payload); }
    };

    const inputCls = "w-full px-3 py-2 border border-slate-200 rounded-md text-sm outline-none focus:ring-2 focus:ring-primary/20 bg-white";

    return (
        <div className="p-8">
            <PageHeader
                title={t("areas.title")}
                description={t("areas.subtitle")}
                actions={[{ label: t("areas.add"), icon: MapPinPlus, onClick: () => setIsDrawOpen(true) }]}
            />

            <AdminTable
                data={areas}
                isLoading={isLoading}
                columns={[
                    {
                        header: t("common.image"),
                        cell: (a) => a.image
                            ? <RemoteImage src={a.image} className="w-10 h-10 rounded object-cover" />
                            : <div className="w-10 h-10 bg-slate-100 rounded" />,
                        enableSorting: false,
                    },
                    { header: t("common.nameEn"), cell: (a) => getTrans(a.translations, "en", "name") || "-" },
                    { header: t("common.nameAr"), cell: (a) => getTrans(a.translations, "ar", "name") || "-" },
                    { header: t("common.slug"), accessorKey: "slug" },
                    { header: t("nav.projects"), cell: (a) => a._count?.projects ?? 0 },
                    {
                        header: t("common.active"),
                        cell: (a) => <span className={`text-xs px-2 py-1 rounded font-semibold ${a.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                            {a.active ? t("common.yes") : t("common.no")}
                        </span>
                    },
                ]}
                onEdit={handleEdit}
                onDelete={(a) => setDeleteTarget(a)}
            />

            <ConfirmDialog
                isOpen={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
                title={t("areas.delete")}
                message={`${t("common.confirmProceed")} "${getTrans(deleteTarget?.translations, "en", "name") || t("areas.title")}"?`}
                confirmText={t("common.delete")}
                isLoading={deleteMutation.isPending}
            />

            <SlideOver
                isOpen={isDrawOpen}
                onClose={handleClose}
                title={editingId ? t("areas.edit") : t("areas.add")}
                footer={
                    <>
                        <button onClick={handleClose} className="px-4 py-2 border rounded hover:bg-slate-50">{t("common.cancel")}</button>
                        <button
                            onClick={handleSubmit(onSubmit)}
                            disabled={createMutation.isPending || updateMutation.isPending}
                            className="px-4 py-2 bg-secondary text-white rounded hover:bg-primary-dark disabled:opacity-60"
                        >
                            {(createMutation.isPending || updateMutation.isPending) ? "..." : (editingId ? t("common.save") : t("common.create"))} {t("areas.title")}
                        </button>
                    </>
                }
            >
                <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label={t("common.slug")} error={errors.slug?.message as string} required>
                            <input
                                {...register("slug")}
                                placeholder="e.g. new-cairo"
                                className={inputCls}
                                disabled={!!editingId}
                            />
                        </FormField>
                        <FormField label={t("common.displayOrder")} error={errors.order?.message as string}>
                            <input type="number" {...register("order")} className={inputCls} />
                        </FormField>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <FormField label={t("common.nameEn")} error={errors.nameEn?.message as string} required>
                            <input {...register("nameEn")} placeholder="English name" className={inputCls} />
                        </FormField>
                        <FormField label={t("common.nameAr")} error={errors.nameAr?.message as string} required>
                            <input {...register("nameAr")} placeholder="الاسم بالعربية" className={inputCls} dir="rtl" />
                        </FormField>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <FormField label={`${t("common.description")} (EN)`} error={errors.descriptionEn?.message as string}>
                            <textarea {...register("descriptionEn")} className={inputCls} rows={3} placeholder="Description in English" />
                        </FormField>
                        <FormField label={`${t("common.description")} (AR)`} error={errors.descriptionAr?.message as string}>
                            <textarea {...register("descriptionAr")} className={inputCls} rows={3} dir="rtl" placeholder="الوصف بالعربية" />
                        </FormField>
                    </div>

                    <FormField label={t("common.image")} error={errors.image?.message as string}>
                        <Controller
                            name="image"
                            control={control}
                            render={({ field }) => (
                                <ImageUpload value={field.value ?? ""} onChange={field.onChange} />
                            )}
                        />
                    </FormField>

                    <label className="flex items-center gap-2">
                        <input type="checkbox" {...register("active")} className="h-4 w-4 rounded" />
                        <span className="text-sm font-medium">{t("common.active")}</span>
                    </label>
                </form>
            </SlideOver>
        </div>
    );
}
